import { Injectable } from '@angular/core';
import { POSType } from '@digilize/shared/definitions/src';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  finalize,
  from,
  map,
  Observable,
  of,
  tap,
  toArray,
} from 'rxjs';

import { CartDto } from '../../infrastructure/dto/products.dto';

import {
  CartLineSubscriberEditDto,
  CartLineSubscriberValidationResultDto,
} from '../../infrastructure/dto/subscription.dto';
import { SubscriptionShopService } from '../../infrastructure/subscription-shop.service';
import {
  allowedSubscriptionTypesForPos,
  isRecurringSubscription,
  startDatePickerShown,
  SubscriptionBillingPeriod,
  SubscriptionProduct,
} from '../../models/subscription';
import { dateOnlyFromApi, dateOnlyToApi, formatDateOnly } from '../date-only';
import { CartState } from './cart.state';

/**
 * One reserved subscription: the product it was reserved from, the period it is billed on and the
 * cart line holding it.
 *
 * The period belongs to the line rather than to the order: a recurring plan may be bought on two
 * billing periods at once (one monthly and two quarterly), so the one global selection cannot say
 * what any given line costs — and writing that selection onto every line at confirmation time is
 * exactly how switching periods used to silently re-bill the lines already reserved.
 */
interface SubscriptionLine {
  product: SubscriptionProduct;
  cartLineId: string;
  /** Null for a non-recurring product (passepartout, punchcard), which has no billing periods. */
  billingPeriod: SubscriptionBillingPeriod | null;
  /**
   * The day this subscription starts (`YYYY-MM-DD`), null while the buyer has not picked one. Per
   * line, as the API has always kept it: two cards of the same plan bought in one order may start on
   * different days, and one date written over all of them would date subscriptions nobody dated
   * (PAUL-3172).
   */
  startDate: string | null;
  /**
   * The first day this line may start (`YYYY-MM-DD`), as the backend resolved it for the period the
   * line carries — null when the product has no floor. Kept on the line rather than looked up per
   * render: a restored line has the cart's own answer for it, which is the one the purchase gate
   * measures that line against.
   */
  firstAllowedStartDate: string | null;
}

/** A subscription in the cart, grouped per product and billing period — what the summary renders. */
export interface SubscriptionSelection {
  product: SubscriptionProduct;
  billingPeriod: SubscriptionBillingPeriod | null;
  quantity: number;
  /**
   * The cart lines this row groups. What ties the row to the money the cart reports per line: only
   * the backend knows what the first term of a periodic subscription actually costs (pro-rated from
   * its start date, setup fee and discount included), so the summary reads it off the line rather
   * than repeating the period's sticker price.
   */
  cartLineIds: string[];
}

/**
 * One cart line as the start-date step renders it: the subscription it holds and the day it was
 * given. One row per line rather than one per product — each line is dated on its own.
 */
export interface SubscriptionStartDateLine {
  cartLineId: string;
  product: SubscriptionProduct;
  /** Null for a non-recurring product; what tells two lines of one plan apart when it is not. */
  billingPeriod: SubscriptionBillingPeriod | null;
  /** The picked calendar day (`YYYY-MM-DD`), null while the buyer has not picked one. */
  startDate: string | null;
  /**
   * The floor the picker must not go below (`YYYY-MM-DD`) — the product's `earliest_start_date`
   * policy resolved by the backend against this line's billing period (PAUL-3163). Null means the
   * product has no floor of its own, which is every non-recurring one; the picker then floors the
   * line on today, the earliest day the shop sells a subscription for.
   */
  firstAllowedStartDate: string | null;
}

/** Two lines belong to the same summary row only when both the product and the period match. */
function sameGroup(line: SubscriptionLine, selection: SubscriptionSelection): boolean {
  return (
    line.product.id === selection.product.id &&
    (line.billingPeriod?.id ?? null) === (selection.billingPeriod?.id ?? null)
  );
}

/**
 * The reserved lines per product and billing period, in the order each combination was first added.
 * One plan on two periods makes two rows — they are billed differently, so they cannot share one.
 */
function groupLines(lines: SubscriptionLine[]): SubscriptionSelection[] {
  const selections: SubscriptionSelection[] = [];
  lines.forEach((line) => {
    const existing = selections.find((selection) => sameGroup(line, selection));
    if (existing) {
      existing.quantity += 1;
      existing.cartLineIds.push(line.cartLineId);
    } else {
      selections.push({
        product: line.product,
        billingPeriod: line.billingPeriod,
        quantity: 1,
        cartLineIds: [line.cartLineId],
      });
    }
  });
  return selections;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionState {
  private readonly _products = new BehaviorSubject<SubscriptionProduct[]>(null);
  readonly products$ = this._products.asObservable();

  private readonly _selectedProduct = new BehaviorSubject<SubscriptionProduct>(null);
  readonly selectedProduct$ = this._selectedProduct.asObservable();

  private readonly _selectedBillingPeriod = new BehaviorSubject<SubscriptionBillingPeriod>(null);
  readonly selectedBillingPeriod$ = this._selectedBillingPeriod.asObservable();

  /**
   * One cart line per purchased subscription; each is assigned to a person later in another app.
   *
   * A basket rather than a single choice: a prepaid order may hold several different non-recurring
   * subscriptions at once, next to its gift cards (PO phase 2), and a recurring order may hold the
   * same plan on more than one billing period. Only a *different* recurring plan is exclusive — see
   * {@link mustRelease}.
   */
  private readonly _lines = new BehaviorSubject<SubscriptionLine[]>([]);

  /**
   * Subscriptions in the cart, grouped per product and billing period, in the order each combination
   * was first added.
   */
  readonly selections$: Observable<SubscriptionSelection[]> = this._lines.pipe(map(groupLines));

  /** How many subscriptions the cart holds in total, across every product in it. */
  readonly quantity$ = this._lines.pipe(map((lines) => lines.length));

  readonly hasSelection$ = this._lines.pipe(map((lines) => lines.length > 0));

  private readonly _loading = new BehaviorSubject<boolean>(false);
  readonly loading$ = this._loading.asObservable();

  /**
   * The lines the start-date step asks a date for, in the order they were reserved — one row each,
   * carrying the day it holds. A product the backend dates itself (`NotRequired`) is left out: an
   * empty field the buyer cannot act on only invites them to wonder.
   *
   * No line is pre-filled with today: a product whose `start_date_requirement` is Required is refused
   * without a *chosen* date (`subscription_start_date_required`), and a date this app invented would
   * satisfy that check on the buyer's behalf. Left null, the backend dates the subscription on the day
   * of purchase, which is what the other two requirement levels ask for anyway.
   */
  readonly startDateLines$: Observable<SubscriptionStartDateLine[]> = this._lines.pipe(
    map((lines) => lines.filter((line) => startDatePickerShown(line.product)))
  );

  /**
   * Whether the start-date step has anything to ask. The reserved lines answer it once there are any,
   * since a cart may hold products that ask and products that do not; before that it is the plan the
   * earlier step highlights, whose line is about to be reserved. Neither one means the cart holds no
   * subscription at all — nothing bought yet, or the last one taken back out, and on a prepaid shop
   * a cart of gift cards alone — and nothing is then asking for a date.
   */
  readonly startDateStepNeeded$: Observable<boolean> = combineLatest([this._lines, this._selectedProduct]).pipe(
    map(([lines, selected]) => {
      if (lines.length) {
        return lines.some((line) => startDatePickerShown(line.product));
      }
      return !!selected && startDatePickerShown(selected);
    }),
    distinctUntilChanged()
  );

  private _subscriberDetailsSaved = false;

  constructor(
    private readonly subscriptionShopService: SubscriptionShopService,
    private readonly cartState: CartState
  ) {}

  /** Null until {@link loadProducts} has answered — "not loaded yet" is not the same as "none". */
  get products(): SubscriptionProduct[] | null {
    return this._products.value;
  }

  /**
   * The product the counters and the summary follow: the one most recently added, or the plan
   * highlighted on the period step before its first line is reserved. A recurring order only ever
   * holds this one product; a prepaid order may hold several, and {@link selections} is what
   * describes those.
   */
  get selectedProduct(): SubscriptionProduct | null {
    return this._selectedProduct.value;
  }

  /**
   * The period most recently added or clicked, which is what a further line of the same plan is
   * reserved on. Not what the order is billed on — the order may hold several periods at once, and
   * only {@link selections} answers that.
   */
  get selectedBillingPeriod(): SubscriptionBillingPeriod | null {
    return this._selectedBillingPeriod.value;
  }

  /** How many subscriptions the cart holds in total, across every product in it. */
  get quantity(): number {
    return this._lines.value.length;
  }

  get hasSelection(): boolean {
    return this._lines.value.length > 0;
  }

  /** Subscriptions in the cart, grouped per product and period — the sync read of {@link selections$}. */
  get selections(): SubscriptionSelection[] {
    return groupLines(this._lines.value);
  }

  /** The distinct products the cart holds at least one line of — one entry however many periods. */
  get productsInCart(): SubscriptionProduct[] {
    const products: SubscriptionProduct[] = [];
    this._lines.value.forEach(({ product }) => {
      if (!products.some((candidate) => candidate.id === product.id)) {
        products.push(product);
      }
    });
    return products;
  }

  /** Synchronous read for click handlers that must not fire a second cart request over a pending one. */
  get loading(): boolean {
    return this._loading.value;
  }

  /**
   * True once every reserved line has been written and accepted by the server-side check — i.e. the
   * step that submits them has run. What tells the step guard the payment step has been earned.
   *
   * A session flag, and deliberately not re-derived on a restore: the shop writes nothing to the
   * lines that a refresh could read it back off, now that it assigns nobody to a subscription. A
   * refresh before the order exists therefore lands the buyer back on the step that submits, which is
   * one Continue away from where they were — and once the order exists {@link SubscriptionFlowAccess}
   * keeps them on the payment step regardless.
   */
  get subscriberDetailsSaved(): boolean {
    return this._subscriberDetailsSaved;
  }

  /**
   * Loads the POS's subscription products, keeping only the types this POS type is allowed to sell:
   * a Subscriptions online shop offers recurring subscriptions, a Prepaid online shop offers
   * passepartouts and punchcards (see {@link allowedSubscriptionTypesForPos}). `allowedProductIds`
   * narrows the list even further (from the `?subscription=` query param); an empty list means "no
   * restriction".
   */
  loadProducts(posId: string, posType: POSType, allowedProductIds: string[] = []): Observable<SubscriptionProduct[]> {
    const allowedTypes = allowedSubscriptionTypesForPos(posType);
    const allowed = new Set(allowedProductIds);
    this._loading.next(true);
    return this.subscriptionShopService.getProducts(posId).pipe(
      map((products) =>
        products.filter(
          (product) =>
            (allowedTypes === null || allowedTypes.includes(product.type)) &&
            (allowed.size === 0 || allowed.has(product.id))
        )
      ),
      tap((products) => this._products.next(products)),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Drops the subscriptions on offer, for a link that offers one product and nothing else (a gift
   * card's `?product=&amount=`). Not {@link reset}: the lines this state may have just restored from
   * a cart stay, only the catalogue the page renders rows from is emptied.
   */
  clearProducts(): void {
    this._products.next([]);
  }

  selectProduct(product: SubscriptionProduct): void {
    this._selectedProduct.next(product);
    // Default the billing period to the first one so the confirmation step has a sensible pre-selection.
    this._selectedBillingPeriod.next(product?.billingPeriods?.[0] ?? null);
  }

  selectBillingPeriod(period: SubscriptionBillingPeriod): void {
    this._selectedBillingPeriod.next(period);
  }

  /**
   * Dates one cart line, keeping the local calendar day the picker handed out — see
   * {@link formatDateOnly}. Every other line keeps its own date.
   */
  setStartDate(cartLineId: string, date: Date | null): void {
    const startDate = date ? formatDateOnly(date) : null;
    this._lines.next(this._lines.value.map((line) => (line.cartLineId === cartLineId ? { ...line, startDate } : line)));
  }

  /**
   * How many of this product the cart holds, on every billing period together — one counter per row
   * of the prepaid selection page, whose products have no periods at all. The period step counts per
   * period instead and reads {@link selections$} for it.
   */
  getQuantity(productId: string): Observable<number> {
    return this._lines.pipe(map((lines) => lines.filter((line) => line.product.id === productId).length));
  }

  /**
   * Reserves one more cart line of the given product on the given billing period, firing the
   * reservation immediately (like the ticket flow's add button). The line is reserved for nobody in
   * particular: who holds it is settled after the purchase, in the web portal.
   *
   * Adding a second product does not disturb the first unless the cart cannot hold both — see
   * {@link mustRelease} — so a prepaid order accumulates the non-recurring subscriptions the buyer
   * picks, the way its gift cards do, and a recurring order accumulates the periods of its plan.
   *
   * `period` says which period this line is billed on, and is what makes a second period possible:
   * it is written onto the line rather than onto the order, so the lines already reserved keep the
   * period they were bought on. Omit it to reserve another line on the currently selected period —
   * `/reservations` rejects a recurring subscription without a `billing_period_id`, so
   * {@link resolveBillingPeriod} always answers with one for a plan that has any.
   */
  addOne(product: SubscriptionProduct, period?: SubscriptionBillingPeriod): Observable<void> {
    const releasing = this.mustRelease(product);
    const billingPeriod = this.resolveBillingPeriod(product, period);
    this._loading.next(true);
    return (releasing ? this.releaseAllLines() : of(undefined)).pipe(
      tap(() => {
        if (this._selectedProduct.value?.id !== product.id) {
          this._selectedProduct.next(product);
        }
        // The period the next line defaults to, not the order's: the reserved lines carry their own.
        if (this._selectedBillingPeriod.value?.id !== billingPeriod?.id) {
          this._selectedBillingPeriod.next(billingPeriod);
        }
      }),
      concatMap(() => this.cartState.reserveSubscription(product.id, billingPeriod?.id ?? null)),
      tap((cartLineId) => {
        this._lines.next([
          ...this._lines.value,
          {
            product,
            cartLineId,
            billingPeriod,
            startDate: null,
            // The floor of the period this line is billed on — the reservation itself reports none, and
            // it is the same resolver either way. A restore later reads the cart's own answer for it.
            firstAllowedStartDate: billingPeriod?.firstAllowedStartDate ?? null,
          },
        ]);
        // The new line has no subscriber data yet, so the confirmation step has to be walked again.
        this._subscriberDetailsSaved = false;
      }),
      map(() => undefined),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Releases the most recently reserved cart line matching what is asked for: of `product` on
   * `period`, of `product` on any period, or simply the most recent line in the cart when neither is
   * given. Fires the removal immediately.
   *
   * The period narrows it because a plan bought on two periods has two counters, and the − of one
   * must not release a line the other one counts.
   */
  removeOne(product?: SubscriptionProduct, period?: SubscriptionBillingPeriod): Observable<void> {
    const lines = this._lines.value;
    const matches = (line: SubscriptionLine): boolean =>
      (!product || line.product.id === product.id) && (!period || line.billingPeriod?.id === period.id);
    const index = lines.reduce((last, line, at) => (matches(line) ? at : last), -1);
    const cartLineId = lines[index]?.cartLineId;
    if (!cartLineId) {
      return of(undefined);
    }
    this._loading.next(true);
    return this.cartState.removeShowlessLine(cartLineId).pipe(
      tap(() => {
        // Re-read the lines instead of splicing the captured array: another +/- may have resolved
        // while this removal was in flight.
        this._lines.next(this._lines.value.filter((line) => line.cartLineId !== cartLineId));
        this.syncSelectionToLines();
      }),
      map(() => undefined),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * Whether reserving `product` has to release what the cart already holds. Only a *different*
   * recurring plan is exclusive: the order is billed period after period against a single mandate, so
   * a second plan replaces the first rather than joining it. The same plan on another billing period
   * is not a second plan and joins the cart — it is compared by product id, so it never reaches here.
   * Non-recurring subscriptions accumulate too — a passepartout and a punchcard are bought together
   * in one order (PO phase 2) — and gift cards live in their own state, guarded by
   * {@link CartIsolation}.
   */
  private mustRelease(product: SubscriptionProduct): boolean {
    const others = this._lines.value.filter((line) => line.product.id !== product.id);
    if (!others.length) {
      return false;
    }
    return isRecurringSubscription(product.type) || others.some((line) => isRecurringSubscription(line.product.type));
  }

  /** Releases every reserved cart line (used when an exclusive plan replaces what the cart holds). */
  private releaseAllLines(): Observable<void> {
    const cartLineIds = this._lines.value.map((line) => line.cartLineId);
    if (!cartLineIds.length) {
      return of(undefined);
    }
    return from(cartLineIds).pipe(
      concatMap((cartLineId) => this.cartState.removeShowlessLine(cartLineId)),
      toArray(),
      tap(() => this._lines.next([])),
      map(() => undefined)
    );
  }

  /**
   * Keeps the selection on something the cart actually holds after a removal: the last remaining
   * line's product and period, or nothing once the cart is empty — which is what returns the counters
   * to zero and blanks the summary.
   */
  private syncSelectionToLines(): void {
    const lines = this._lines.value;
    if (!lines.length) {
      this.selectProduct(null);
      return;
    }
    const last = lines[lines.length - 1];
    if (!lines.some((line) => line.product.id === this._selectedProduct.value?.id)) {
      this.selectProduct(last.product);
    }
    // The period whose last line was just released must not stay selected: the next add would reserve
    // on a period the cart no longer holds.
    if (!lines.some((line) => line.billingPeriod?.id === this._selectedBillingPeriod.value?.id)) {
      this._selectedBillingPeriod.next(last.billingPeriod);
    }
  }

  /**
   * The period a new line of `product` is billed on: the one the caller named, else the current
   * selection when that belongs to this product (another line of the same plan on the same period),
   * else the product's own first period. Never another product's period — a cart holding several
   * non-recurring products must not have the leftover selection of another one written onto them —
   * and null only for a product with no periods at all, which is every non-recurring one.
   */
  private resolveBillingPeriod(
    product: SubscriptionProduct,
    period?: SubscriptionBillingPeriod
  ): SubscriptionBillingPeriod | null {
    const own = (candidate: SubscriptionBillingPeriod | null | undefined): boolean =>
      !!candidate && product.billingPeriods.some((their) => their.id === candidate.id);
    if (own(period)) {
      return period;
    }
    if (own(this._selectedBillingPeriod.value)) {
      return this._selectedBillingPeriod.value;
    }
    return product.billingPeriods[0] ?? null;
  }

  /** The billing period id to write on a line — its own, checked against the product it holds. */
  private billingPeriodIdFor(line: SubscriptionLine): string | null {
    const period = line.billingPeriod;
    return period && line.product.billingPeriods.some((candidate) => candidate.id === period.id) ? period.id : null;
  }

  /**
   * Persists the chosen billing period and start date on every cart line — and no subscriber, see
   * {@link bodyFor} — then runs server-side validation. Resolves to the per-line validation results.
   */
  saveSubscriberDetails(): Observable<CartLineSubscriberValidationResultDto[]> {
    const cartId = this.cartState.getCartIdValue();
    // A prepaid order may hold gift cards only: there is no line to persist or validate, and nothing
    // is outstanding — the flag says "no line is waiting to be submitted", which is what gates the
    // payment step (see {@link SubscriptionFlowAccess}).
    if (!this._lines.value.length) {
      this._subscriberDetailsSaved = true;
      return of([]);
    }
    if (!cartId) {
      throw new Error('Subscription cart line is missing');
    }
    this._loading.next(true);
    // The edits below are what the charge is computed from, so an order created on an earlier visit
    // to the payment step no longer matches the lines it would charge for.
    this.cartState.invalidatePendingOrder();
    return from(this._lines.value).pipe(
      concatMap((line) => this.subscriptionShopService.editCartLine(line.cartLineId, this.bodyFor(line))),
      toArray(),
      concatMap(() => this.subscriptionShopService.validateCart(cartId)),
      tap((results) => (this._subscriberDetailsSaved = results.every((result) => result.is_valid))),
      // The edits above are what the first-term charge is computed from — a pro-rated period runs
      // from the start date just written — so everything the reservation reported about money is
      // stale from here on. Re-read it now, before the payment step shows an amount next to a
      // charge the buyer authorises. A failed refresh must not fail the step: the lines are saved.
      concatMap((results) =>
        this.cartState.refreshCartMoney().pipe(
          catchError(() => of(null)),
          map(() => results)
        )
      ),
      finalize(() => this._loading.next(false))
    );
  }

  /**
   * The body for one cart line: what the subscription was bought as, and nobody it was bought for.
   * Both the start date and the billing period are the line's own — the date the buyer gave this line
   * and the period it was reserved on — so a plan bought twice keeps two dates, and one bought monthly
   * and quarterly keeps both periods.
   *
   * A line the buyer gave no date is submitted on its first allowed day rather than as null: a null is
   * read as the day of purchase, which a product whose floor lies days ahead does not allow. Both
   * requirements that leave a line undated mean the same thing by it — `NotRequired` never asks, and
   * `Optional` lets the field be left empty — namely that the subscription starts as early as the
   * product permits. Null is sent only where there is no floor at all, where the day of purchase is
   * the right answer.
   *
   * The shop assigns no person to a subscription, not even the buyer: a subscription is personalised
   * later and elsewhere, in the web portal, where its holder — who may well not be the buyer —
   * completes their own details. Writing the buyer here would both name the wrong holder and reach the
   * portal read-only (`locked_fields`), locking the right one out of correcting it.
   *
   * Every personal field is therefore sent as an explicit null rather than left out: this is a full
   * replace of a line nobody else writes, so the nulls also clear anything an earlier pass of this
   * step put there. Omitting the keys would say the same thing to the current API; the nulls keep it
   * explicit and match {@link CartLineSubscriberEditDto}.
   */
  private bodyFor(line: SubscriptionLine): CartLineSubscriberEditDto {
    const startDate = line.startDate ?? line.firstAllowedStartDate;
    return {
      billing_period_id: this.billingPeriodIdFor(line),
      start_date: startDate ? dateOnlyToApi(startDate) : null,
      email: null,
      first_name: null,
      last_name: null,
      birthday: null,
      gender: null,
      address: null,
      zipcode: null,
      city: null,
      country: null,
    };
  }

  /**
   * The period a restored line is billed on, as the cart names it. A line whose subscriber names no
   * period yet keeps the product's first one — what a fresh selection defaults to — so a recurring
   * line is never edited back to the server without a `billing_period_id`.
   */
  private restoredBillingPeriod(
    product: SubscriptionProduct,
    billingPeriodId: string | null
  ): SubscriptionBillingPeriod | null {
    return (
      product.billingPeriods.find((candidate) => candidate.id === billingPeriodId) ?? product.billingPeriods[0] ?? null
    );
  }

  /**
   * Rebuilds the subscription selection from a persisted cart so a page refresh keeps the user on
   * their current step with the cart already populated. Products must be loaded first; lines for a
   * product that is not one of them — a gift card, above all — belong to another state and are left
   * alone. Returns true when at least one subscription line was restored. Deliberately minimal: it
   * only restores the products, their quantities and the billing period and start date each line
   * carries — there is no per-step navigation guard.
   */
  restoreFromCart(cart: CartDto): boolean {
    const products = this._products.value ?? [];
    const restored = (cart.summary?.lines ?? []).flatMap((line) => {
      const product = products.find((candidate) => candidate.id === line.product_id);
      if (!product) {
        return [];
      }
      const billingPeriod = this.restoredBillingPeriod(product, line.subscriber?.billing_period_id ?? null);
      return [
        {
          product,
          cartLineId: line.id,
          billingPeriod,
          // The day the API named, read off its string so no offset can shift it. A line reserved
          // but not yet confirmed carries none and stays undated.
          startDate: line.subscriber?.start_date ? dateOnlyFromApi(line.subscriber.start_date) : null,
          // The cart's own floor for this line, resolved against the period the line carries. The
          // period's own value stands in only where the cart names none — an older backend, or a line
          // whose period could not be matched.
          firstAllowedStartDate: line.subscriber?.first_allowed_start_date
            ? dateOnlyFromApi(line.subscriber.first_allowed_start_date)
            : billingPeriod?.firstAllowedStartDate ?? null,
        },
      ];
    });
    if (!restored.length) {
      return false;
    }
    this._lines.next(restored);
    // The last line's product and period, which is where a live add leaves the selection too. A
    // recurring order only ever holds one product — though possibly on several periods; a prepaid one
    // may hold several products, and it is the per-row counters, not this, that tell those apart.
    const last = restored[restored.length - 1];
    this.selectProduct(last.product);
    if (last.billingPeriod) {
      this.selectBillingPeriod(last.billingPeriod);
    }
    // {@link subscriberDetailsSaved} is deliberately left alone: the lines carry nobody, so there is
    // nothing on them a restore could read that step's completion back off.
    return true;
  }

  reset(): void {
    this._products.next(null);
    this._selectedProduct.next(null);
    this._selectedBillingPeriod.next(null);
    this._lines.next([]);
    this._loading.next(false);
    this._subscriberDetailsSaved = false;
  }
}
